/**
 * Avgiftstexter för tillvalen (MFF-akademin, padel & tennis) och fritids.
 *
 * Alla belopp kommer från skolsystemets prislista via pristjänsten
 * (`getSitePricing` → `applySitePricing`). Ingen sida får skriva in egna
 * siffror – saknas priset returnerar funktionerna null och sidan visar
 * ingen siffra alls i stället för en gammal.
 */
import {
  activityFeeForTerm,
  afterschoolPricesForTerm,
  defaultPricedTerm,
  formatEur,
} from "@/lib/fees";

export type ActivityKey = "football" | "padel";
export type FeeLocale = "sv" | "en" | "es";

/** Terminsavgift för ett tillval, för den termin sajten visar priser för. */
export function activityTermFee(key: ActivityKey): number | null {
  const term = defaultPricedTerm();
  if (!term) return null;
  return activityFeeForTerm(key, term);
}

const PER_TERM: Record<FeeLocale, string> = {
  sv: "per termin",
  en: "per term",
  es: "por periodo",
};

/** T.ex. "1 000 € per termin". Null när priset saknas. */
export function activityFeeText(key: ActivityKey, locale: FeeLocale = "sv"): string | null {
  const fee = activityTermFee(key);
  if (fee === null) return null;
  return `${formatEur(fee)} ${PER_TERM[locale]}`;
}

/** Fritidsavgifter för den aktuella terminen. */
export function afterschoolFees(): {
  term: number | null;
  year: number | null;
  shortWeek: number | null;
  shortMonth: number | null;
} | null {
  const term = defaultPricedTerm();
  if (!term) return null;
  return afterschoolPricesForTerm(term);
}

/** Fritidstext, t.ex. "750 € per termin eller 1 300 € per läsår." */
export function afterschoolFeeText(locale: FeeLocale = "sv"): string | null {
  const p = afterschoolFees();
  if (!p || p.term === null) return null;
  const term = `${formatEur(p.term)} ${PER_TERM[locale]}`;
  const year =
    p.year === null
      ? null
      : locale === "sv"
        ? `${formatEur(p.year)} per läsår`
        : locale === "en"
          ? `${formatEur(p.year)} per school year`
          : `${formatEur(p.year)} por curso escolar`;
  const base =
    locale === "sv"
      ? year
        ? `Avgiften är ${term} eller ${year}.`
        : `Avgiften är ${term}.`
      : locale === "en"
        ? year
          ? `The fee is ${term} or ${year}.`
          : `The fee is ${term}.`
        : year
          ? `La tarifa es de ${term} o ${year}.`
          : `La tarifa es de ${term}.`;
  if (p.shortWeek === null && p.shortMonth === null) return base;
  const week = p.shortWeek === null ? null : formatEur(p.shortWeek);
  const month = p.shortMonth === null ? null : formatEur(p.shortMonth);
  const short =
    locale === "sv"
      ? ` Vid korttidsvistelse gäller ${week ?? "–"} per vecka eller ${month ?? "–"} per månad.`
      : locale === "en"
        ? ` For short stays it is ${week ?? "–"} per week or ${month ?? "–"} per month.`
        : ` Para estancias cortas, ${week ?? "–"} por semana o ${month ?? "–"} por mes.`;
  return base + short;
}
